import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { GameMode } from '../types';
import AnimatedTabs from './AnimatedTabs';
import './ProfileModal.css'; // Reuse modal base styles
import './HowItWorksModal.css';

interface HowItWorksModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialMode?: GameMode;
}

export default function HowItWorksModal({ isOpen, onClose, initialMode = 'classic' }: HowItWorksModalProps) {
    const [activeMode, setActiveMode] = useState<GameMode>(initialMode);

    const MODES: { id: GameMode; label: string }[] = [
        { id: 'classic', label: 'Classic' },
        { id: 'flip', label: 'Flip' },
        { id: 'blitz', label: 'Blitz' },
        { id: 'bridge', label: 'Bridge' },
    ];

    const renderClassic = () => (
        <div className="how-it-works-panel">
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/dictionary.png" alt="Guess" />
                </div>
                <div className="step-content">
                    <h6>1. Make a Guess</h6>
                    <p>Enter any word to see how close it is to the secret concept.</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/flame.png" alt="Heat" />
                </div>
                <div className="step-content">
                    <h6>2. Check the Temperature</h6>
                    <p>Warmer colors mean your word is semantically closer. "Fire" is close to "Heat", but far from "Ice".</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/check.png" alt="Win" />
                </div>
                <div className="step-content">
                    <h6>3. Find the Word</h6>
                    <p>Keep guessing until you find the exact secret word!</p>
                </div>
            </div>
        </div>
    );

    const renderFlip = () => (
        <div className="how-it-works-panel">
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/arrows.png" alt="Flip" />
                </div>
                <div className="step-content">
                    <h6>1. Flip Cards</h6>
                    <p>Guess words related to the main theme to reveal hidden cards.</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/question-sign.png" alt="Clue" />
                </div>
                <div className="step-content">
                    <h6>2. Use Clues</h6>
                    <p>Earn points for good guesses and use them to buy letter clues.</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/check.png" alt="Win" />
                </div>
                <div className="step-content">
                    <h6>3. Clear the Board</h6>
                    <p>Find all the top associations to complete the puzzle.</p>
                </div>
            </div>
        </div>
    );

    const renderBlitz = () => (
        <div className="how-it-works-panel">
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/sun.png" alt="Lobby" />
                </div>
                <div className="step-content">
                    <h6>1. Join the Lobby</h6>
                    <p>Wait for other players to join the live session.</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/flame.png" alt="Race" />
                </div>
                <div className="step-content">
                    <h6>2. Race to Solve</h6>
                    <p>Guess words to find the secret concept before your opponents!</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/check-2.png" alt="Win" />
                </div>
                <div className="step-content">
                    <h6>3. Win the Blitz</h6>
                    <p>Be the first to find the word to win the round.</p>
                </div>
            </div>
        </div>
    );

    const renderBridge = () => (
        <div className="how-it-works-panel">
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/bridge.png" alt="Connect" />
                </div>
                <div className="step-content">
                    <h6>1. Connect Words</h6>
                    <p>Find a word that is at least 35% similar to the current word.</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/arrows.png" alt="Build" />
                </div>
                <div className="step-content">
                    <h6>2. Build the Bridge</h6>
                    <p>Each correct guess becomes a new stepping stone.</p>
                </div>
            </div>
            <div className="step-card">
                <div className="step-icon-wrapper">
                    <img src="/static/check.png" alt="Win" />
                </div>
                <div className="step-content">
                    <h6>3. Complete the Path</h6>
                    <p>Connect the Start word to the Goal word to win!</p>
                </div>
            </div>
        </div>
    );

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="profile-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="profile-modal how-it-works-modal"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="profile-modal-header">
                            <h2 className="profile-modal-title">How to Play</h2>
                            <button className="profile-modal-close" onClick={onClose}>×</button>
                        </div>

                        <div className="how-it-works-scroll-area">
                            <div className="ai-explainer">
                                <span className="ai-explainer-icon" role="img" aria-label="Brain">🧠</span>
                                <div>
                                    <h5>AI-Powered Word Game</h5>
                                    <p>
                                        This game uses artificial intelligence to understand the <strong>meaning</strong> of words,
                                        not just their spelling.
                                    </p>
                                </div>
                            </div>

                            <AnimatedTabs
                                tabs={MODES.map(mode => ({ id: mode.id, label: mode.label }))}
                                activeTab={activeMode}
                                onTabChange={setActiveMode}
                                layoutId="how-it-works-tabs"
                            />

                            <div className="how-it-works-content">
                                {activeMode === 'classic' && renderClassic()}
                                {activeMode === 'flip' && renderFlip()}
                                {activeMode === 'blitz' && renderBlitz()}
                                {activeMode === 'bridge' && renderBridge()}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
